import { api } from '../config/api';

// The brand's STYLE CATEGORY: which of the 11 Style Universe galaxies its
// store stands in on the map. The brand picks it on its datasheet — nothing
// derives it from the products (CEO decision, documents#658).
//
// API (outfino-api services/brand_profile_update.js):
//   write  PUT /brands/:id { style_category }   permission editBrand; one of
//          the 11 keys below (aliases like 'Goth/Alt' fold to the key),
//          null / '' clears it; anything else → 400 invalid_parameters.
//          Every write stamps style_category_source = 'manual'.
//          In the Shopify-embedded build the same call maps to
//          PUT /shopify/admin/brand (same handler).
//   read   GET /brands/:id        → style_category (snake, the column)
//          /user/me stores[]      → styleCategory (camel)
//          PUT /brands/:id answer → the brand, style_category included

/// The 11 galaxies in map order (STYLE_MAP_LAYOUT_V1_ORDER, the registry's
/// slot), so neighbours on the map are neighbours here. The colour is the
/// galaxy registry's seed (style_galaxies) — galaxies carry NO emoji (CEO
/// rule; the API sends `emoji: ""`), the colour is the only accent; the names live in i18n
/// (`styleCategory.name.<key>`), copied from the mobile app's galaxy names
/// (historyStyleVertex* — the map labels).
export const STYLE_CATEGORIES = [
  { key: 'streetwear', color: '#F2994A' },
  { key: 'y2k', color: '#FF6FD8' },
  { key: 'goth_alt', color: '#8E5CF0' },
  { key: 'techwear', color: '#3FC6E8' },
  { key: 'experimental', color: '#B8F25C' },
  { key: 'boho_indie', color: '#E8B04A' },
  { key: 'vintage_retro', color: '#D9825B' },
  { key: 'old_money', color: '#C9B07A' },
  { key: 'minimalist', color: '#E8E4DC' },
  { key: 'casual', color: '#6FA8FF' },
  { key: 'sporty', color: '#4ADE80' },
];
export const STYLE_CATEGORY_KEYS = STYLE_CATEGORIES.map((c) => c.key);

// The alias spellings the API folds (utils/ootd_categories.js
// CATEGORY_BIJECTION), so an older value reads as its galaxy.
const ALIASES = {
  goth: 'goth_alt',
  'goth alt': 'goth_alt',
  'goth/alt': 'goth_alt',
  boho: 'boho_indie',
  'boho indie': 'boho_indie',
  'boho/indie': 'boho_indie',
  vintage: 'vintage_retro',
  'vintage retro': 'vintage_retro',
  'vintage/retro': 'vintage_retro',
  'old money': 'old_money',
};

/// A stored / typed value → one of the 11 keys, or null (unset / unknown).
export function normalizeStyleCategory(v) {
  if (typeof v !== 'string') return null;
  const s = v.trim().toLowerCase();
  if (!s) return null;
  if (STYLE_CATEGORY_KEYS.includes(s)) return s;
  const folded = s.replace(/[\s_-]+/g, ' ');
  if (ALIASES[folded]) return ALIASES[folded];
  const underscored = folded.replace(/ /g, '_');
  return STYLE_CATEGORY_KEYS.includes(underscored) ? underscored : null;
}

const KEYS = ['style_category', 'styleCategory'];
const carries = (o) => !!o && typeof o === 'object' && KEYS.some((k) => k in o);
const valueOf = (o) => normalizeStyleCategory(o[KEYS.find((k) => k in o)]);

/// The brand's category from the payloads at hand: the brand detail
/// (GET /brands/:id, the fresh read) wins over the session brand (/user/me
/// stores[]). Null = none picked (or an API that does not send the field).
export function resolveStyleCategory(brand, detail = null) {
  if (carries(detail)) return valueOf(detail);
  if (carries(brand)) return valueOf(brand);
  return null;
}

/// Save the category: PUT /brands/:id { style_category }. Resolves to the
/// stored key. The API answers with the brand; an answer without the field
/// (an API build that does not know it — it would silently drop the key) or
/// with a different value did not take the change, so it is a failure and
/// the picker rolls back.
export async function updateBrandStyleCategory(brandId, key) {
  const value = normalizeStyleCategory(key);
  if (!value) {
    const err = new Error('style_category_invalid');
    err.status = 0;
    throw err;
  }
  const res = await api(`/brands/${brandId}`, { method: 'PUT', body: { style_category: value } });
  const brand = carries(res) ? res : carries(res?.brand) ? res.brand : null;
  if (res && typeof res === 'object' && (!brand || valueOf(brand) !== value)) {
    const err = new Error('style_category_not_saved');
    err.status = 0;
    throw err;
  }
  return value;
}
